import { useState } from "react";
import { fieldClass } from "./fields";

// Locale is es-MX throughout the CRM (see lib/format-mxn.ts).
const LOCALE = "es-MX";

/**
 * The price cell of a catalog row.
 *
 * **Edits pesos, stores centavos.** The integer is the only representation that
 * ever leaves this component: 19.90 is not representable in binary floating
 * point, and this number is summed into an order total a real person pays in
 * cash. The one division and the one multiplication both live here.
 *
 * **Formatted at rest, plain while typing.** Unfocused it reads `$ 1,450` (or
 * `$ 19.90` when there are centavos); focused it shows `1450`, so the caret
 * never fights separators being inserted under it. The text typed is held as
 * typed while focused instead of being derived from the number on every
 * keystroke — otherwise "19." round-trips through `Number` and loses its point.
 *
 * A comma is a THOUSANDS separator here and is dropped, never a decimal point:
 * the field displays commas as thousands, so reading "1,450" back as 1.45 would
 * reprice a product at a hundredth of its value.
 *
 * An empty field is `null`, which is NOT zero: null means "no price on file"
 * and the agent then says nothing about cost, while 0 is a real price (a free
 * trial class) that it will quote.
 *
 * `onChange` fires per keystroke (the catalog debounces its save); `onCommit`
 * fires on blur and Enter, when the price is saved at once.
 */
export function PriceInput({
  valueCents,
  currency,
  onChange,
  onCommit,
  disabled,
  invalid,
  ariaLabel,
}: {
  valueCents: number | null;
  /** ISO-4217 code of the catalog; only its symbol is shown. */
  currency: string;
  onChange: (cents: number | null) => void;
  onCommit?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  ariaLabel: string;
}): React.JSX.Element {
  // Non-null only while focused: the text the admin is typing.
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="relative w-28 shrink-0">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-[13.5px] text-ink-400"
      >
        {currencySymbol(currency)}
      </span>
      <input
        className={`${fieldClass({ invalid })} w-full pl-6 text-right tabular-nums`}
        value={editing ?? formatCents(valueCents)}
        disabled={disabled}
        inputMode="decimal"
        placeholder="0"
        aria-label={ariaLabel}
        onFocus={() => setEditing(centsToPlain(valueCents))}
        onChange={(e) => {
          const next = e.target.value;
          // Digits, thousands commas and at most one decimal point. Rejecting the
          // keystroke rather than sanitizing it keeps the caret where it was put.
          if (next !== "" && !/^[\d,]*\.?\d{0,2}$/.test(next)) return;
          setEditing(next);
          onChange(textToCents(next));
        }}
        onBlur={() => {
          setEditing(null);
          onCommit?.();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") onCommit?.();
        }}
      />
    </div>
  );
}

/** `"MXN"` → `"$"`, `"EUR"` → `"€"`; an unknown code shows the code itself. */
function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat(LOCALE, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

/** `145000` → `"1,450"`; `1990` → `"19.90"`; null → `""`. */
function formatCents(cents: number | null): string {
  if (cents === null) return "";
  const fraction = cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(cents / 100);
}

/** `145000` → `"1450"`; `1990` → `"19.90"`; null → `""`. */
function centsToPlain(cents: number | null): string {
  if (cents === null) return "";
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

/**
 * `"1,450.5"` → `145050`; `""` → null.
 *
 * Rounds rather than truncates: `19.99 * 100` is `1998.9999999999998` in
 * floating point, and `Math.trunc` would quietly charge a centavo less.
 */
function textToCents(text: string): number | null {
  const normalized = text.replace(/,/g, "").trim();
  if (normalized === "" || normalized === ".") return null;
  const pesos = Number(normalized);
  if (!Number.isFinite(pesos)) return null;
  return Math.round(pesos * 100);
}
